/* Zettler Digital Cart Tools - Auto Gift, cart layer.
   Keeps the free gift in the cart whenever a qualifying Auto Gift code is applied there, so express /
   Shop Pay checkouts start with the gift already in the cart. Mirrors the checkout extension's rules:
   - code applied (and accepted by Shopify) + non-gift subtotal >= minSubtotal -> exactly one tagged gift line
   - otherwise -> remove any tagged gift line
   A gift the buyer added themselves (untagged) is left alone; the discount function makes one unit free.

   Oct 2026: once the buyer heads to checkout, a bridged drawer code is handed off and never un-bridged.
   Zipify clears its discount_code cookie on the way to checkout; that used to look like "code removed",
   so the code and the gift were stripped from the cart just before checkout loaded. */
(() => {
  if (window.__autoGiftCartLoaded) return;
  window.__autoGiftCartLoaded = true;

  const AUTO_ADDED_KEY = '_auto_added';
  const AUTO_ADDED_LABEL_KEY = '_auto_added_reason';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const parseConfig = (raw) => {
    let v = raw;
    if (typeof v === 'string') {
      try { v = JSON.parse(v); } catch (e) { return null; }
    }
    if (!v || typeof v !== 'object' || v.enabled === false) return null;
    const offers = (Array.isArray(v.offers) ? v.offers : [])
      .filter((o) => o && o.offerEnabled !== false && o.code && typeof o.variantId === 'string')
      .map((o) => ({
        id: String(o.id),
        code: String(o.code).trim().toUpperCase(),
        variantNum: Number(String(o.variantId).split('/').pop()),
        quantity: Math.max(parseInt(o.quantity, 10) || 1, 1),
        minCents: Math.round((Number(o.minSubtotal) || 0) * 100),
        eligible: (Array.isArray(o.eligibleProductIds) ? o.eligibleProductIds : []).map((id) => Number(String(id).split('/').pop())),
      }));
    return offers.length ? offers : null;
  };

  const offers = parseConfig(window.__autoGiftConfig);
  if (!offers) return;

  const tag = (item) => (item.properties && item.properties[AUTO_ADDED_KEY]) || null;

  // Zipify's cart-drawer discount field doesn't attach the code to the cart - it only stores it in
  // Shopify's `discount_code` cookie, which applies at checkout. Read that cookie so a code entered
  // in the drawer is bridged onto the cart (and the gift added before checkout).
  const BRIDGE_KEY = 'agc_bridged_code';
  const cookieCodes = () => {
    const m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    if (!m) return [];
    let v = m[1];
    try { v = decodeURIComponent(v); } catch (e) { /* keep raw */ }
    return v.split(',').map((c) => c.trim().toUpperCase()).filter(Boolean);
  };
  const store = {
    get() { try { return sessionStorage.getItem(BRIDGE_KEY); } catch (e) { return null; } },
    set(v) { try { v ? sessionStorage.setItem(BRIDGE_KEY, v) : sessionStorage.removeItem(BRIDGE_KEY); } catch (e) { /* noop */ } },
  };
  let running = false;
  let again = false;
  let ownRequest = false;
  // True from the moment the buyer heads to checkout (or the page unloads) until they come back.
  let leaving = false;

  async function api(path, body) {
    ownRequest = true;
    try {
      const res = await fetch(root + path, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      return res.json();
    } finally {
      ownRequest = false;
    }
  }

  async function reconcile() {
    if (running) { again = true; return; }
    running = true;
    let changed = false;
    try {
      let cart = await api('cart.js');

      // Bridge a drawer-entered code (cookie only) onto the cart, and undo it when the drawer removes it.
      // Never undo while leaving for checkout: Zipify clears the cookie on the way out, which is a
      // hand-off, not a removal.
      const native = (cart.discount_codes || []).map((d) => String(d.code).trim().toUpperCase());
      const fromCookie = cookieCodes();
      const bridged = store.get();
      const toBridge = offers.find((o) => fromCookie.includes(o.code) && !native.includes(o.code));
      if (toBridge) {
        await api('cart/update.js', { discount: native.concat(toBridge.code).join(',') });
        store.set(toBridge.code);
        cart = await api('cart.js');
      } else if (!leaving && bridged && native.includes(bridged) && !fromCookie.includes(bridged)) {
        await api('cart/update.js', { discount: native.filter((c) => c !== bridged).join(',') });
        store.set(null);
        cart = await api('cart.js');
        changed = true;
      }

      const applied = (cart.discount_codes || [])
        .filter((d) => d && d.applicable)
        .map((d) => String(d.code).trim().toUpperCase());
      const offer = offers.find((o) => applied.includes(o.code));

      // While leaving, only ever ADD the gift - never strip it. The checkout extension and the discount
      // function enforce the rules at checkout.
      const tagged = cart.items.filter((i) => tag(i));
      const buyerAdded = offer && cart.items.some((i) => !tag(i) && i.variant_id === offer.variantNum);

      let wanted = false;
      if (offer && !buyerAdded) {
        const subtotal = cart.items
          .filter((i) => !tag(i) && i.variant_id !== offer.variantNum)
          .filter((i) => !offer.eligible.length || offer.eligible.includes(i.product_id))
          .reduce((sum, i) => sum + (i.original_line_price || 0), 0);
        wanted = subtotal >= offer.minCents;
      }

      // Remove every tagged line that shouldn't be there (wrong offer, extra copies, not earned).
      const keep = wanted
        ? tagged.find((i) => tag(i) === offer.id && i.variant_id === offer.variantNum)
        : null;
      const updates = {};
      if (!leaving) {
        tagged.forEach((i) => { if (i !== keep) updates[i.key] = 0; });
      }
      if (keep && keep.quantity !== offer.quantity) updates[keep.key] = offer.quantity;
      if (Object.keys(updates).length) {
        await api('cart/update.js', { updates });
        changed = true;
      }

      if (wanted && !keep) {
        await api('cart/add.js', {
          items: [{
            id: offer.variantNum,
            quantity: offer.quantity,
            properties: { [AUTO_ADDED_KEY]: offer.id, [AUTO_ADDED_LABEL_KEY]: offer.code },
          }],
        });
        changed = true;
      }
    } catch (e) {
      console.warn('[auto-gift-cart]', e);
    } finally {
      running = false;
    }
    if (changed && !leaving) refreshCartUI();
    if (again) { again = false; schedule(); }
  }

  function refreshCartUI() {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auto-gift-cart' });
      }
    } catch (e) { /* noop */ }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('auto-gift:changed', { bubbles: true }));
    // Zipify cart drawer: re-read the cart so the gift line shows without reopening the drawer.
    try {
      const zi = window.Zipify && window.Zipify.Cart && window.Zipify.Cart.instance;
      if (zi && typeof zi.updateCart === 'function') zi.updateCart();
    } catch (e) { /* noop */ }
  }

  let timer = null;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, 350);
  }

  // Watch every cart / discount request made by the theme or apps (fetch and XHR).
  const isCartUrl = (url) => /\/(cart|discount)(\/|\.js|\?|$)/.test(String(url || ''));
  const origFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const p = origFetch.apply(this, arguments);
    if (!ownRequest && isCartUrl(url) && !/\/cart\.js(\?|$)/.test(url)) p.then(schedule, () => {});
    return p;
  };
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (!ownRequest && isCartUrl(url) && String(method).toUpperCase() !== 'GET') {
      this.addEventListener('loadend', schedule);
    }
    return origOpen.apply(this, arguments);
  };

  // Zipify drawer discount field: re-check shortly after Apply / Enter / remove.
  document.addEventListener('click', (e) => {
    const el = e.target && e.target.closest && e.target.closest('[class*="ocu-discount"], [class*="ocu-cart"] [class*="discount"]');
    if (el) setTimeout(schedule, 900);
  }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target && e.target.classList && e.target.classList.contains('ocu-discount-apply__input')) {
      setTimeout(schedule, 900);
    }
  }, true);

  // Heading to checkout: hand the bridged code off (it now belongs to checkout like any cart code),
  // then make sure the gift is in the cart. Covers the theme's and Zipify's checkout buttons and
  // express / Shop Pay buttons.
  // `handOff` only on a real checkout action - a plain page change must keep the bridge so the drawer
  // can still remove the code on the next page.
  const startLeaving = (handOff) => {
    leaving = true;
    if (handOff === true) store.set(null);
  };
  document.addEventListener('click', (e) => {
    const el = e.target && e.target.closest && e.target.closest(
      '[name="checkout"], a[href*="/checkout"], [href*="/discount/"], .shopify-payment-button, ' +
      'shopify-accelerated-checkout, shopify-accelerated-checkout-cart, ' +
      '[class*="ocu-checkout"], [class*="ocu"] [class*="checkout"], [id*="checkout"]'
    );
    if (el) {
      startLeaving(true);
      reconcile();
    }
  }, true);
  document.addEventListener('submit', (e) => {
    const action = (e.target && e.target.getAttribute && e.target.getAttribute('action')) || '';
    if (/\/checkout|\/cart\/?(\?|$)/.test(action)) startLeaving(true); // not /cart/add
  }, true);
  window.addEventListener('pagehide', startLeaving);
  window.addEventListener('beforeunload', startLeaving);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
  // Back from checkout (bfcache): the code, if still applied, is a normal cart code now.
  window.addEventListener('pageshow', () => { leaving = false; schedule(); });
})();